/**
 * 比較ダッシュボード (エリア / 路線) の横棒グラフ。
 *
 * 行 = 調査項目、行の中にエリア / 路線ごとの棒を並べる。2026-09-16 (オーナー指示):
 * 棒は**評価の区分で積み上げ**、区分は濃淡と模様で区別し、境目は白いスリットで区切る。
 * ポインタを乗せた部分の値はその場に出す (スマホでは触れたときに出る)。
 * 色はエリア / 路線を表し、`REPORT_PALETTE` を薄くして使う。
 *
 * html2canvas (PDF 出力) は経由しないので、CSS の模様をそのまま使える。
 */
import { POLARITY_LABELS, type EvaluationPolarity } from '@citizen-walk/shared-types';
import {
  barFill,
  barFillBackgroundSize,
  POLARITY_DISPLAY_ORDER,
  POLARITY_HATCH_DESCRIPTIONS,
  STACK_SEPARATOR_STYLE,
} from './barFillStyles';
import { PolarityLegend } from './PolarityLegend';
import { useChartTooltip } from './useChartTooltip';

export interface AreaComparisonBarChartRow {
  key: string;
  label: string;
  color: string;
  /** `reportNames` と同じ順序。 */
  values: number[];
  /** レポートごとの評価の内訳 (`values` と同じ単位)。積み上げに使う。 */
  polarities?: ({ positive: number; negative: number; neutral: number } | null)[];
}

export interface AreaComparisonBarChartProps {
  rows: AreaComparisonBarChartRow[];
  reportNames: string[];
  /** 評価の区分で積み上げるか (既定 true)。false なら合計だけの 1 本。 */
  stackByPolarity?: boolean;
  /** 値の単位 (ツールチップ用、例「件/地点」)。 */
  unit?: string;
  /** 評価の区分の表示名 (評価の定義の `polarityLabels`)。 */
  polarityLabels?: Record<EvaluationPolarity, string>;
}

/** エリア比較ダッシュボードの色割当 (棒グラフ・地図で共有、`AreaComparisonMap.tsx` からも参照)。 */
export const REPORT_PALETTE = ['#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2'];

const POLARITY_ORDER = POLARITY_DISPLAY_ORDER;

function format(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function AreaComparisonBarChart({
  rows,
  reportNames,
  stackByPolarity = true,
  unit = '',
  polarityLabels = POLARITY_LABELS,
}: AreaComparisonBarChartProps) {
  const tooltip = useChartTooltip();
  if (rows.length === 0 || reportNames.length === 0) {
    return <p className="text-sm text-slate-400">比較できるデータがありません。</p>;
  }
  const maxValue = Math.max(1, ...rows.flatMap((r) => r.values));
  // 合計が多いカテゴリを上に (AreaBarChart の値順ソートと同じ考え方)。
  const sorted = [...rows].sort(
    (a, b) => b.values.reduce((s, v) => s + v, 0) - a.values.reduce((s, v) => s + v, 0),
  );
  const hasPolarity = stackByPolarity && rows.some((r) => r.polarities?.some((p) => p));

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        {reportNames.map((name, i) => (
          <span key={name} className="flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full border border-slate-300"
              style={barFill(REPORT_PALETTE[i % REPORT_PALETTE.length]!)}
            />
            {name}
          </span>
        ))}
      </div>

      {/* 評価の見分け方はグラフの上 (色分けの凡例のすぐ下) に置く。下だとスクロールしないと見えない。 */}
      {hasPolarity && (
        <PolarityLegend
          className="-mt-2 mb-3"
          colorNote="色はエリア / 路線を表します。"
          polarityLabels={polarityLabels}
        />
      )}

      <div className="grid grid-cols-1 gap-3">
        {sorted.map((row) => (
          <div key={row.key} className="min-w-0">
            <span className="mb-1 flex items-center gap-1.5 text-xs text-slate-700 sm:text-sm">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full border border-slate-300"
                style={{ backgroundColor: row.color }}
              />
              <span className="truncate">{row.label}</span>
            </span>
            <div className="grid grid-cols-1 gap-1 pl-2 sm:pl-4">
              {row.values.map((v, i) => {
                const reportColor = REPORT_PALETTE[i % REPORT_PALETTE.length]!;
                const polarity = stackByPolarity ? (row.polarities?.[i] ?? null) : null;
                const evaluated = polarity
                  ? polarity.positive + polarity.negative + polarity.neutral
                  : 0;
                const rest = Math.max(0, v - evaluated);
                const lines = polarity
                  ? [
                      ...POLARITY_ORDER.map((k) => ({
                        label: polarityLabels[k],
                        value: `${format(polarity[k])}${unit}`,
                        color: barFill(reportColor, k).background,
                      })),
                      { label: '合計', value: `${format(v)}${unit}` },
                    ]
                  : [{ label: '値', value: `${format(v)}${unit}` }];
                const title = `${row.label}／${reportNames[i]}`;
                return (
                  <div
                    key={reportNames[i]}
                    className="flex items-center gap-2 text-xs"
                    onMouseMove={(e) => tooltip.show(e, title, lines)}
                    onMouseLeave={tooltip.hide}
                    onTouchStart={(e) => tooltip.show(e, title, lines)}
                    onTouchEnd={tooltip.hide}
                  >
                    <span className="h-3 min-w-0 flex-1 overflow-hidden rounded bg-slate-100">
                      <span
                        className="flex h-full overflow-hidden rounded"
                        style={{ width: `${(v / maxValue) * 100}%` }}
                      >
                        {polarity && v > 0 ? (
                          <>
                            {POLARITY_ORDER.map((k) =>
                              polarity[k] > 0 ? (
                                <span
                                  key={k}
                                  className="block h-full cursor-default transition-opacity hover:opacity-75"
                                  style={{
                                    width: `${(polarity[k] / v) * 100}%`,
                                    ...barFill(reportColor, k),
                                    backgroundSize: barFillBackgroundSize(k),
                                    ...STACK_SEPARATOR_STYLE,
                                  }}
                                  onMouseMove={(e) => {
                                    e.stopPropagation();
                                    tooltip.show(e, `${title}／${polarityLabels[k]}`, [
                                      { label: '値', value: `${format(polarity[k])}${unit}` },
                                      { label: '合計', value: `${format(v)}${unit}` },
                                      { label: '模様', value: POLARITY_HATCH_DESCRIPTIONS[k] },
                                    ]);
                                  }}
                                />
                              ) : null,
                            )}
                            {rest > 0 && (
                              <span
                                className="block h-full"
                                style={{ width: `${(rest / v) * 100}%`, ...barFill(reportColor) }}
                              />
                            )}
                          </>
                        ) : (
                          <span className="block h-full w-full" style={barFill(reportColor)} />
                        )}
                      </span>
                    </span>
                    <span className="w-12 shrink-0 text-right font-semibold tabular-nums text-slate-700">
                      {format(v)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {tooltip.node}
    </div>
  );
}
